import React, { useState } from 'react'
import { Box, Text } from '@chakra-ui/react'
import { FaNoteSticky } from 'react-icons/fa6'

import ChatInfoAccordionItem from '../info-accordion-item'
import InfoEmptyPlaceholder from '../info-empty-placeholder'
import InfoLoadingPlaceholder from '../info-loading-placeholder'
import AsyncWrapper from '~components/shared/wrappers/async-wrapper'
import ChatClientDetailsNoteItem from './note-item'
import {
    ChatFieldsFragment,
    ClientNoteFieldsFragment,
    PatientNoteFieldsFragment,
    useGetClientNotesQuery,
} from '~graphql/generated/graphql'

interface Props {
    chat: ChatFieldsFragment
}

type PatientNotesGroup = {
    id: string
    name: string | null | undefined
    notes: PatientNoteFieldsFragment[]
}

export default function ChatClientDetailsNotes({ chat }: Props) {
    const { client } = chat

    const [isOpen, setIsOpen] = useState(false)

    const { data, loading } = useGetClientNotesQuery({
        variables: { id: client?.id ?? '' },
        skip: !client || !isOpen,
    })

    const clientNotes = (data?.client?.client_notes ?? []).filter(
        (note): note is ClientNoteFieldsFragment =>
            note.is_active && !!note.text,
    )
    const patientGroups: PatientNotesGroup[] = (
        data?.client?.patients ?? []
    ).flatMap(patient => {
        const notes = (patient.patient_notes ?? []).filter(
            (note): note is PatientNoteFieldsFragment =>
                note.is_active && !!note.text,
        )
        return notes.length > 0
            ? [{ id: patient.id, name: patient.name, notes }]
            : []
    })

    const isEmpty = clientNotes.length === 0 && patientGroups.length === 0

    return (
        <ChatInfoAccordionItem
            icon={FaNoteSticky}
            title="PMS Notes"
            isOpenState={isOpen}
            setIsOpenState={setIsOpen}
        >
            <Box px={4} pt={2} pb={3}>
                <AsyncWrapper
                    loading={loading}
                    loadingComponent={<InfoLoadingPlaceholder />}
                    empty={!loading && isEmpty}
                    emptyComponent={<InfoEmptyPlaceholder item="notes" />}
                >
                    <Box display="flex" flexDir="column" gap={2}>
                        {sortNotes(clientNotes).map(note => (
                            <ChatClientDetailsNoteItem
                                key={note.id}
                                note={note}
                            />
                        ))}
                        {patientGroups.map(group => (
                            <Box key={group.id}>
                                <Text
                                    fontSize="11px"
                                    fontWeight="semibold"
                                    color="gray.500"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    mb={1}
                                >
                                    {group.name ?? 'Patient'}
                                </Text>
                                <Box
                                    display="flex"
                                    flexDir="column"
                                    gap={2}
                                >
                                    {sortNotes(group.notes).map(note => (
                                        <ChatClientDetailsNoteItem
                                            key={note.id}
                                            note={note}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </AsyncWrapper>
            </Box>
        </ChatInfoAccordionItem>
    )
}

function sortNotes<
    T extends ClientNoteFieldsFragment | PatientNoteFieldsFragment,
>(notes: T[]): T[] {
    return [...notes].sort(
        (a, b) =>
            (b.noted_at ?? b.created_at) - (a.noted_at ?? a.created_at),
    )
}
